import { useEffect, useState } from 'react';
import { Plus, User } from 'lucide-react';
import { createClientContact, fetchClientContacts } from '@/lib/api';
import type { ClientContact } from '@/lib/types';

interface Props {
  clientId: string;
  value: ClientContact | null;
  onChange: (contact: ClientContact | null) => void;
}

/**
 * ContactPicker - kies of maak een contactpersoon voor de klant.
 * - Auto-fetch contacts voor de klant
 * - Dropdown bij meerdere
 * - "Nieuwe toevoegen" knop opent inline mini-form
 * - Auto-select primary (is_primary=true) als er nog niets gekozen is
 */
export function ContactPicker({ clientId, value, onChange }: Props) {
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchClientContacts(clientId)
      .then((list) => {
        if (cancelled) return;
        setContacts(list);
        if (!value && list.length > 0) {
          // auto-select primary, anders eerste
          const primary = list.find((c) => c.is_primary) ?? list[0];
          onChange(primary);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function handleCreate(input: {
    name: string;
    email: string;
    phone: string;
    role: string;
  }) {
    setError(null);
    try {
      const created = await createClientContact({
        client_id: clientId,
        name: input.name,
        email: input.email || null,
        phone: input.phone || null,
        role: input.role || null,
        is_primary: contacts.length === 0,
      });
      setContacts((prev) => [...prev, created]);
      onChange(created);
      setShowNew(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aanmaken mislukt');
    }
  }

  if (loading) {
    return <div className="text-xs text-bmade-500">Contactpersonen laden...</div>;
  }

  return (
    <div className="space-y-2">
      {contacts.length > 0 ? (
        <div className="space-y-1">
          {contacts.map((c) => {
            const active = value?.id === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onChange(c)}
                className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  active
                    ? 'border-accent-400 bg-accent-500/10 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                    : 'border-bmade-200 bg-white text-bmade-800 hover:border-bmade-400 dark:border-bmade-700 dark:bg-bmade-950 dark:text-bmade-200'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <User className="h-3 w-3 shrink-0 opacity-60" />
                    <span className="font-medium">{c.name}</span>
                    {c.is_primary && (
                      <span className="rounded bg-accent-500/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-accent-700 dark:text-accent-300">
                        primair
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-bmade-500">
                    {[c.role, c.email, c.phone || c.mobile].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-bmade-500">Nog geen contactpersonen voor deze klant.</p>
      )}

      {!showNew ? (
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-bmade-300 px-3 py-1.5 text-xs text-bmade-600 hover:border-accent-400 hover:bg-accent-50 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-400 dark:hover:bg-accent-500/10 dark:hover:text-accent-300"
        >
          <Plus className="h-3 w-3" /> Nieuwe contactpersoon
        </button>
      ) : (
        <NewContactForm
          onCancel={() => setShowNew(false)}
          onCreate={(input) => void handleCreate(input)}
        />
      )}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}

function NewContactForm({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (input: { name: string; email: string; phone: string; role: string }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');
  const canSave = name.trim().length > 1;

  const inputCls =
    'w-full rounded-md border border-bmade-200 bg-white px-2 py-1.5 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white';

  return (
    <div className="space-y-2 rounded-md border border-bmade-200 bg-bmade-50/50 p-3 dark:border-bmade-800 dark:bg-bmade-950/30">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
        Nieuwe contactpersoon
      </div>
      <input
        autoFocus
        type="text"
        placeholder="Naam (verplicht)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={inputCls}
      />
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={inputCls}
      />
      <input
        type="tel"
        placeholder="Telefoon"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        className={inputCls}
      />
      <input
        type="text"
        placeholder="Functie / rol"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className={inputCls}
      />
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-bmade-200 px-2 py-1 text-xs text-bmade-600 hover:bg-bmade-100 dark:border-bmade-700 dark:text-bmade-300 dark:hover:bg-bmade-800"
        >
          Annuleer
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onCreate({ name: name.trim(), email, phone, role })}
          className="rounded bg-accent-500 px-3 py-1 text-xs font-medium text-white hover:bg-accent-600 disabled:opacity-40"
        >
          Opslaan
        </button>
      </div>
    </div>
  );
}
